import { useState, useEffect, useCallback } from 'react';
import { api } from '../features/auth/authStore';
import { timerPersistence } from '../services/timerPersistence';
import toast from 'react-hot-toast';

interface ActiveTimer {
  id: string;
  taskId?: string;
  taskTitle?: string;
  description: string;
  startTime: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface TimeEntry {
  id: string;
  taskId?: string;
  description: string;
  startTime: Date;
  endTime?: Date;
  duration: number;
  billable: boolean;
  status: string;
}

interface UseTimeTrackerReturn {
  activeTimer: ActiveTimer | null;
  elapsedTime: number;
  loading: boolean;
  startTimer: (taskId?: string, description?: string) => Promise<void>;
  stopTimer: () => Promise<void>;
  loadActiveTimer: () => Promise<void>;
  createTimeEntry: (entry: Partial<TimeEntry>) => Promise<void>;
  getTimeEntries: (filters?: any) => Promise<TimeEntry[]>;
  getTimeStats: (startDate?: Date, endDate?: Date) => Promise<any>;
  hasRecoveredTimer: boolean;
}

const TIMER_SYNC_INTERVAL = 30000; // 30 seconds

export const useTimeTracker = (userId?: string): UseTimeTrackerReturn => {
  const [activeTimer, setActiveTimer] = useState<ActiveTimer | null>(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [loading, setLoading] = useState(false);
  const [hasRecoveredTimer, setHasRecoveredTimer] = useState(false);



  // Timer tick effect with persistence
  useEffect(() => {
    let interval: NodeJS.Timeout;

    if (activeTimer && userId) {
      interval = setInterval(() => {
        const elapsed = timerPersistence.getElapsedTime(activeTimer);
        setElapsedTime(elapsed);

        // Save to localStorage periodically
        timerPersistence.saveTimer(activeTimer, userId);
      }, 1000);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [activeTimer, userId]);

  const loadActiveTimer = useCallback(async () => {
    if (!userId) return;

    try {
      const { serverTimer, localTimer, needsRecovery } = await timerPersistence.syncWithServer(userId);

      if (needsRecovery && localTimer) {
        // Try to recover the local timer
        const recoveredTimer = await timerPersistence.recoverTimer(localTimer, userId);
        if (recoveredTimer) {
          setActiveTimer(recoveredTimer);
          setElapsedTime(timerPersistence.getElapsedTime(recoveredTimer));
          setHasRecoveredTimer(true);
          toast.success('Timer recovered from previous session');
          return;
        }
      }

      if (serverTimer && localTimer && serverTimer.id !== localTimer.id) {
        // Resolve conflict - prefer server timer
        const resolvedTimer = await timerPersistence.resolveConflict(serverTimer, localTimer, userId);
        setActiveTimer(resolvedTimer);
        setElapsedTime(timerPersistence.getElapsedTime(resolvedTimer));
        return;
      }

      if (serverTimer) {
        setActiveTimer(serverTimer);
        setElapsedTime(timerPersistence.getElapsedTime(serverTimer));
        timerPersistence.saveTimer(serverTimer, userId);
      } else if (localTimer) {
        // Use local timer if no server timer
        setActiveTimer(localTimer);
        setElapsedTime(timerPersistence.getElapsedTime(localTimer));
      } else {
        setActiveTimer(null);
        setElapsedTime(0);
        timerPersistence.saveTimer(null, userId);
      }
    } catch (error) {
      console.error('Error loading active timer:', error);
      // Fallback to local timer if available
      if (userId) {
        const localTimer = timerPersistence.loadTimer(userId);
        if (localTimer) {
          setActiveTimer(localTimer);
          setElapsedTime(timerPersistence.getElapsedTime(localTimer));
        }
      }
    }
  }, [userId]);

  const startTimer = useCallback(async (taskId?: string, description?: string) => {
    if (!userId) return;

    setLoading(true);
    try {
      const response = await api.post('/time-entries/start-timer', {
        taskId: taskId || undefined,
        description: description || 'Timer session'
      });

      if (response.data.success) {
        const timer = {
          ...response.data.data,
          startTime: new Date(response.data.data.startTime)
        };
        setActiveTimer(timer);
        setElapsedTime(0);
        timerPersistence.saveTimer(timer, userId);
        toast.success('Timer started');
      }
    } catch (error: any) {
      console.error('Error starting timer:', error);

      let message = 'Failed to start timer';
      if (error.response?.data?.error?.message) {
        const serverMessage = error.response.data.error.message;
        if (serverMessage.includes('active timer')) {
          message = 'You already have an active timer running. Please stop it first before starting a new one.';
        } else {
          message = serverMessage;
        }
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }

      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [userId]);

  const stopTimer = useCallback(async () => {
    if (!activeTimer || !userId) return;

    setLoading(true);
    try {
      const response = await api.patch(`/time-entries/${activeTimer.id}/stop`);

      if (response.data.success) {
        const stoppedEntry = response.data.data;
        setActiveTimer(null);
        setElapsedTime(0);
        setHasRecoveredTimer(false);
        timerPersistence.saveTimer(null, userId);

        const hours = Math.floor(stoppedEntry.duration / 60);
        const minutes = stoppedEntry.duration % 60;
        const timeStr = hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;

        toast.success(`Timer stopped - ${timeStr} recorded`);
        return stoppedEntry;
      }
    } catch (error: any) {
      console.error('Error stopping timer:', error);

      let message = 'Failed to stop timer';
      if (error.response?.data?.error?.message) {
        message = error.response.data.error.message;
      } else if (error.response?.data?.message) {
        message = error.response.data.message;
      }

      toast.error(message);
      throw error;
    } finally {
      setLoading(false);
    }
  }, [activeTimer, userId]);

  const createTimeEntry = useCallback(async (entry: Partial<TimeEntry>) => {
    setLoading(true);
    try {
      const response = await api.post('/time-entries', {
        taskId: entry.taskId || undefined,
        description: entry.description,
        startTime: entry.startTime || new Date(),
        endTime: entry.endTime,
        duration: entry.duration,
        billable: entry.billable !== false
      });

      if (response.data.success) {
        toast.success('Time entry created');
        return response.data.data;
      }
    } catch (error: any) {
      console.error('Error creating time entry:', error);
      toast.error('Failed to create time entry');
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const getTimeEntries = useCallback(async (filters?: any): Promise<TimeEntry[]> => {
    try {
      const params = new URLSearchParams();
      if (filters?.startDate) params.append('startDate', filters.startDate.toISOString());
      if (filters?.endDate) params.append('endDate', filters.endDate.toISOString());
      if (filters?.taskId) params.append('taskId', filters.taskId);

      const response = await api.get(`/time-entries/my-entries?${params}`);
      if (response.data.success) {
        return response.data.data.entries || [];
      }
      return [];
    } catch (error) {
      console.error('Error fetching time entries:', error);
      return [];
    }
  }, []);

  const getTimeStats = useCallback(async (startDate?: Date, endDate?: Date) => {
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('startDate', startDate.toISOString());
      if (endDate) params.append('endDate', endDate.toISOString());

      const response = await api.get(`/time-entries/stats?${params}`);
      if (response.data.success) {
        return response.data.data;
      }
      return null;
    } catch (error) {
      console.error('Error fetching time stats:', error);
      return null;
    }
  }, []);

  // Periodic sync with server
  useEffect(() => {
    if (!activeTimer) return;

    const syncInterval = setInterval(() => {
      loadActiveTimer();
    }, TIMER_SYNC_INTERVAL);

    return () => clearInterval(syncInterval);
  }, [activeTimer, loadActiveTimer]);

  // Handle window focus/blur events for better sync
  useEffect(() => {
    const handleFocus = () => {
      if (activeTimer) {
        loadActiveTimer();
      }
    };

    const handleBeforeUnload = () => {
      if (activeTimer && userId) {
        timerPersistence.saveTimer(activeTimer, userId);
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [activeTimer, loadActiveTimer, userId]);

  // Load active timer on hook initialization
  useEffect(() => {
    if (userId) {
      loadActiveTimer();
    }
  }, [loadActiveTimer, userId]);

  return {
    activeTimer,
    elapsedTime,
    loading,
    startTimer,
    stopTimer,
    loadActiveTimer,
    createTimeEntry,
    getTimeEntries,
    getTimeStats,
    hasRecoveredTimer
  };
};
